import {
  ActiveLaneIcon,
  CheckCircleIcon,
  ChevronIcon,
  ExclamationCircleIcon,
  SideDropArrowIcon,
  Theme,
  TruckSimpleIcon,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import TRUCKLANE_MINIMUM_MODE from '../../enums/trucklane-minimum-mode';
import OfferReviewShiftCard from './offer-review-shift-card';

const useStyles = createUseStyles({
  container: {
    overflowY: 'auto',
    paddingLeft: '31px',
    '&:not(:last-child)': {
      marginBottom: '16px',
    },
  },
  rows: {
    display: 'flex',
  },
  card: {
    display: 'flex',
    padding: '12px',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: Theme.green5,
    },
  },
  shiftCard: {
    padding: '12px',
  },
  columns: {
    display: 'flex',
    flexDirection: 'column',
  },
  spacedRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  minimumsDetails: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontStyle: 'italic',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '-0.154px',
  },
  fullWidth: {
    width: '100%',
  },
  border: {
    border: `1px ${Theme.borderColor} solid`,
    borderRadius: '4px',
  },
  informationText: {
    fontSize: '16px',
    fontWeight: 'normal',
    lineHeight: '24px',
    letterSpacing: '-0.176px',
    alignContent: 'center',
  },
  expandCard: {
    cursor: 'pointer',
  },
  activeLane: {
    border: `1px solid ${Theme.primaryColor}`,
    backgroundColor: Theme.green5,
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    '& span': {
      color: Theme.primaryColor,
      width: 'fit-content',
      padding: '2px 8px 2px 0px',
      marginBottom: 0,
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: '0.75rem',
      whiteSpace: 'nowrap',
    },
    '& svg': {
      stroke: Theme.primaryColor,
      fill: 'transparent',
      width: 20,
      height: 20,
      marginLeft: 8,
      marginRight: 6,
    },
  },
  inactiveLane: {
    extend: 'activeLane',
    border: `1px solid ${Theme.grey80}`,
    backgroundColor: Theme.grey5,
    '& span': {
      color: Theme.textColorPrimary,
    },
    '& svg': {
      stroke: 'none',
    },
  },
  arrow: {
    marginTop: 18,
    height: 8,
    width: 16,
    stroke: Theme.textColorPrimary,
    marginRight: 8,
    transition: 'transform 0.3s',
  },
  offerTable: {
    borderCollapse: 'separate',
    textAlign: 'left',
    '& th': {
      paddingRight: '24px',
      color: Theme.tableHeaderTextColor,
      textTransform: 'uppercase',
      fontWeight: 500,
      fontSize: '12px',
    },
    '& td': {
      paddingRight: '24px',
      fontSize: '14px',
    },
  },
  tableTitle: {
    color: Theme.green,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16px',
    marginLeft: '2px',
  },
  divider: {
    marginTop: '12px',
  },
  revenueIcon: {
    height: '14px',
    width: '14px',
    marginRight: '4px',
  },
  trucklaneRevenueInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'end',
  },
  revenueAmount: {
    textAlign: 'right',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '26px',
    letterSpacing: '-0.198px',
  },
  revenueMode: {
    color: Theme.grey80,
    textAlign: 'right',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '16px',
    textTransform: 'uppercase',
  },
  revenueMinimum: {
    textAlign: 'right',
    fontSize: '14px',
    fontStyle: 'italic',
    fontWeight: 400,
    lineHeight: '16px',
    margin: '8px 0 4px 0',
  },
});

const OfferReviewTrucklaneCard = ({ user, trucklane, buyerName, setShowActivatedLaneBanner }) => {
  const classes = useStyles();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isTrucklaneActive = trucklane.awardedWeight > 0;

  const calculateTotals = (tl, statuses) => {
    return tl.originalOfferListings.reduce(
      (acc, ol) => {
        const offerData = ol.offerShifts.reduce(
          (olAcc, olCurrent) => {
            if (statuses.includes(olCurrent.status))
              return {
                weight: olAcc.weight + olCurrent.unitGrossWeight * olCurrent.shiftedQuantity,
                pallets: olAcc.pallets + olCurrent.shiftedQuantity / olCurrent.casesPerPallet,
                totalCost: olAcc.totalCost + olCurrent.unitCost * olCurrent.shiftedQuantity,
                totalPrice: olAcc.totalPrice + olCurrent.unitPrice * olCurrent.shiftedQuantity,
              };
            return olAcc;
          },
          { weight: 0, pallets: 0, totalCost: 0, totalPrice: 0 },
        );
        return {
          weight: acc.weight + offerData.weight,
          pallets: acc.pallets + offerData.pallets,
          totalCost: acc.totalCost + offerData.totalCost,
          totalPrice: acc.totalPrice + offerData.totalPrice,
        };
      },
      { weight: 0, pallets: 0, totalCost: 0, totalPrice: 0 },
    );
  };

  const opportunityTableData = useMemo(() => {
    const { weight, pallets, totalCost, totalPrice } = calculateTotals(trucklane, ['PROPOSED']);

    const costRecovery = (totalPrice / totalCost) * 100;
    return [
      { header: 'Weight', value: weight ? `${accounting.formatNumber(weight, 2)} lbs` : '0 lbs', key: 'Opportunity' },
      { header: 'Pallets', value: pallets ? accounting.formatNumber(pallets) : '0', key: 'Opportunity' },
      { header: 'Revenue', value: totalPrice ? accounting.formatMoney(totalPrice) : '$0.00', key: 'Opportunity' },
      {
        header: 'Recovery',
        value: costRecovery ? `${accounting.formatNumber(costRecovery, 2)}%` : '0%',
        key: 'Opportunity',
      },
    ];
  }, [trucklane]);

  const awardedTableData = useMemo(() => {
    const { weight, pallets, totalCost, totalPrice } = calculateTotals(trucklane, ['ACCEPTED']);
    const { deliveryPallets, pickupPallets, deliveryWeight, pickupWeight } = user?.site?.trucklaneMinimums || {};
    const logisticsTerm = trucklane.logisticsTerm.toLowerCase();
    const truckType = trucklane.truckType.toLowerCase();
    const minPallets = logisticsTerm === 'pickup' ? pickupPallets[truckType] : deliveryPallets[truckType];
    const minWeight = logisticsTerm === 'pickup' ? pickupWeight[truckType] : deliveryWeight[truckType];

    trucklane.awardedWeight += weight;
    trucklane.awardedPallets += pallets;
    trucklane.awardedTotalPrice += totalPrice;
    trucklane.awardedTotalCost += totalCost;
    trucklane.awardedRevenue += totalPrice;
    trucklane.costRecovery = (trucklane.awardedTotalPrice / trucklane.awardedTotalCost) * 100;

    const palletsMinimumMet = trucklane.awardedPallets >= minPallets;
    const weightMinimumMet = trucklane.awardedWeight >= minWeight;

    const MinPalletsIcon = palletsMinimumMet ? CheckCircleIcon : ExclamationCircleIcon;
    const MinWeightIcon = weightMinimumMet ? CheckCircleIcon : ExclamationCircleIcon;

    return [
      {
        header: 'Weight',
        value: trucklane.awardedWeight ? `${accounting.formatNumber(trucklane.awardedWeight, 2)} lbs` : '0 lbs',
        key: 'Awarded',
        details:
          minWeight > 0 ? (
            <span className={classes.minimumsDetails} style={{ color: weightMinimumMet ? Theme.primaryColor : Theme.orange }}>
              {<MinWeightIcon className={classes.revenueIcon} />} {`${accounting.formatNumber(minWeight)} lbs min`}
            </span>
          ) : (
            <span />
          ),
      },
      {
        header: 'Pallets',
        value: trucklane.awardedPallets ? accounting.formatNumber(trucklane.awardedPallets) : '0',
        key: 'Awarded',
        details:
          minPallets > 0 ? (
            <span className={classes.minimumsDetails} style={{ color: palletsMinimumMet ? Theme.primaryColor : Theme.orange }}>
              {<MinPalletsIcon className={classes.revenueIcon} />} {`${accounting.formatNumber(minPallets)} pal min`}
            </span>
          ) : (
            <span />
          ),
      },
      {
        header: 'Recovery',
        value: trucklane.costRecovery ? `${accounting.formatNumber(trucklane.costRecovery, 2)}%` : '0%',
        key: 'Awarded',
      },
    ];
  }, [trucklane]);

  const trucklaneTableGenerator = useCallback(
    (tableData) => {
      const tableHeaders = [];
      const tableValues = [];
      const tableDetails = [];

      tableData.forEach((data) => {
        tableHeaders.push(<th key={`${data.header}-${data.key}`}>{data.header}</th>);
        tableValues.push(<td key={`${data.header}-value-${data.key}`}>{data.value}</td>);
        if (data.details) tableDetails.push(<td key={`${data.header}-details-${data.key}`}>{data.details}</td>);
      });

      return (
        <table className={classes.offerTable}>
          <thead>
            <tr>{tableHeaders}</tr>
          </thead>
          <tbody>
            <tr>{tableValues}</tr>
            {tableDetails.length > 0 && <tr>{tableDetails}</tr>}
          </tbody>
        </table>
      );
    },
    [classes.offerTable],
  );

  const getTrucklaneMode = () => {
    const revenueSource = trucklane.logisticsTerm.toLowerCase() === 'pickup' ? 'pickupRevenue' : 'deliveryRevenue';
    const trucklaneMinimum = user?.site?.trucklaneMinimums[revenueSource][trucklane.truckType.toLowerCase()] || 0;
    const minimumMet = trucklane.awardedRevenue >= trucklaneMinimum;

    let trucklaneMode = 'Trucklane Settings Not Set';
    if (user?.site?.trucklaneMinimums?.mode === TRUCKLANE_MINIMUM_MODE.NET_REVENUE) trucklaneMode = 'Awarded Net Revenue';
    if (user?.site?.trucklaneMinimums?.mode === TRUCKLANE_MINIMUM_MODE.TOTAL_REVENUE) trucklaneMode = 'Awarded Revenue';

    const trucklaneAwardedRevenue = isTrucklaneActive ? trucklane.awardedRevenue : 0;
    return (
      <div className={classes.trucklaneRevenueInfo}>
        <span className={classes.revenueAmount}>{`${accounting.formatMoney(trucklaneAwardedRevenue)}`}</span>
        <span className={classes.revenueMode}>{trucklaneMode}</span>
        {trucklaneMinimum > 0 && (
          <span className={classes.revenueMinimum}>
            {minimumMet ? <CheckCircleIcon className={classes.revenueIcon} /> : <ExclamationCircleIcon className={classes.revenueIcon} />}
            <span style={{ color: minimumMet ? Theme.primaryColor : Theme.orange }}>{`${accounting.formatMoney(trucklaneMinimum)} min`}</span>
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.border}>
        <div className={classes.card} onClick={() => setShowSuggestions(!showSuggestions)}>
          <div className={classes.expandCard}>
            <ChevronIcon className={classes.arrow} direction={showSuggestions ? 'up' : 'down'} />
          </div>
          <div className={classes.fullWidth}>
            <div>
              <div className={classes.spacedRow}>
                <span className={classes.informationText}>{trucklane.shiftSellerLocationName}</span>
                {isTrucklaneActive ? (
                  <div className={classes.activeLane}>
                    <ActiveLaneIcon /> <span>ACTIVE TRUCK LANE</span>
                  </div>
                ) : (
                  <div className={classes.inactiveLane}>
                    <TruckSimpleIcon style={{ transform: 'scaleX(-1)' }} /> <span>INACTIVE TRUCK LANE</span>
                  </div>
                )}
              </div>
              <div>
                <SideDropArrowIcon style={{ marginRight: '8px' }} />
                <span className={classes.informationText}>{trucklane.buyerDestinationName}</span>
              </div>
            </div>
            <div className={classes.divider} />
            <div className={classes.spacedRow}>
              <div className={classes.rows}>
                <div>
                  <span className={classes.tableTitle}>OPPORTUNITY</span>
                  {trucklaneTableGenerator(opportunityTableData)}
                </div>
                <div>
                  <span className={classes.tableTitle}>CURRENTLY AWARDED</span>
                  {trucklaneTableGenerator(awardedTableData)}
                </div>
              </div>
              {getTrucklaneMode()}
            </div>
          </div>
        </div>
        {showSuggestions && (
          <div className={classes.shiftCard}>
            <div className={classes.divider} />
            {trucklane.originalOfferListings.map((shiftedOffer) => {
              return (
                <OfferReviewShiftCard
                  key={shiftedOffer._id}
                  offer={shiftedOffer}
                  buyerName={buyerName}
                  trucklane={trucklane}
                  setShowActivatedLaneBanner={setShowActivatedLaneBanner}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

OfferReviewTrucklaneCard.propTypes = {
  user: PropTypes.object,
  trucklane: PropTypes.object,
  buyerName: PropTypes.string,
  setShowActivatedLaneBanner: PropTypes.func,
};

export default OfferReviewTrucklaneCard;
